// Copyright ©️ 2026 eVolve MEP, LLC

import type { AutodeskElementTagId, AutodeskExternalId } from '@evolvemep/api-ts';

import type { TagPropertyValue } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/types';
import { getBasicStoreListeners } from 'helpers/getBasicStoreListeners';

/**
 * A state primarily derived from {@link RenderedTagState}, but with only the properties needed for React rendering.
 */
export type RenderedTagsSnapshot = {
  elementTagId: AutodeskElementTagId;
  externalId: AutodeskExternalId;
  /**
   * The tag's property names paired with their resolved values, ordered for display.
   *
   * @remarks `null` while loading
   */
  propertyValues: readonly TagPropertyValue[] | null;
  /** Pixel X within `viewer.container` (center of label). */
  x: number;
  /** Pixel Y within `viewer.container` (center of label). */
  y: number;
  visible: boolean;
};

/**
 * Minimal store that bridges `TagRenderingManager`'s computed display state to the React layer.
 * `TagRenderingManager` writes via `setTags`; React reads via `useSyncExternalStore`.
 */
export const createRenderedTagsStore = () => {
  let tags: readonly RenderedTagsSnapshot[] = [];
  const { subscribe, notifyListeners } = getBasicStoreListeners();

  return {
    getSnapshot: (): readonly RenderedTagsSnapshot[] => tags,
    subscribe,
    /** Called by `TagRenderingManager` whenever projected positions or display state changes. */
    setTags: (next: RenderedTagsSnapshot[]) => {
      tags = next;
      notifyListeners();
    },
    clear: () => {
      tags = [];
      notifyListeners();
    },
  } as const;
};

export type RenderedTagsStore = ReturnType<typeof createRenderedTagsStore>;
